import type { TFunction } from 'i18next';
import { dropEmptyHeadings, isNavItemVisible, type NavGates, type NavVisibilityContext } from '@/config/navVisibility';
import {
  LayoutDashboard,
  FolderOpen,
  Plug,
  Building2,
  MapPin,
  Package,
  Award,
  FileText,
  PieChart,
  Settings,
  Users,
  Leaf,
  Receipt,
  Wallet,
  Banknote,
  CreditCard,
  BadgeCheck,
  Activity,
  MessageSquare,
  BadgeDollarSign,
  FileBarChart,
  ClipboardCheck,
  ClipboardList,
  ArrowRightLeft,
  Landmark,
  Percent,
  HandHeart,
  ShieldCheck,
  ShoppingCart,
  Recycle,
  Trash2,
  Flame,
  Repeat,
  Gauge,
  Link2,
  CalendarRange,
  History,
  MessagesSquare,
  UserCog,
  type LucideIcon,
} from "lucide-react";

/**
 * Visibility gates (`requiresSuperAdmin`, `requiresOrgAdmin`, `requiresScope`,
 * `featureFlag`) come from `NavGates` and are evaluated by the one shared
 * `isNavItemVisible` (config/navVisibility.ts).
 */
export interface NavigationItem extends NavGates {
  type?: 'item';
  name: string;
  href: string;
  icon: LucideIcon;
  spaceBefore?: boolean;
  /**
   * Which live count to render after the label (docs/NAVIGATION.md "Badges").
   * `MainNavigation` resolves it — `channel_unread` is the current project's
   * unread channel comments. Plain items only, never the heading/group union.
   */
  badgeKey?: 'channel_unread';
}

export interface NavigationGroup {
  type: 'group';
  name: string;
  icon: LucideIcon;
  storageKey: string;
  items: NavigationItem[];
}

export interface NavigationHeading {
  type: 'heading';
  name: string;
}

export type SecondaryNavEntry = NavigationItem | NavigationGroup | NavigationHeading;

export interface SecondaryNavigationConfig {
  [key: string]: SecondaryNavEntry[];
}

// Factory function for secondary navigation config with i18n support
export const getSecondaryNavigationConfig = (t: TFunction): SecondaryNavigationConfig => ({
  // Accounting section — every entry carries the `accounting` scope, the same
  // as the /accounting route guard (Impact moved out to /impact).
  "/accounting": [
    { name: t("nav.accounting.overview"), href: "/accounting/overview", icon: LayoutDashboard, requiresScope: 'accounting' },
    { name: t("nav.accounting.bankAccounts"), href: "/accounting/bank-accounts", icon: Landmark, requiresScope: 'accounting' },
    { name: t("nav.accounting.creditCards"), href: "/accounting/credit-cards", icon: CreditCard, requiresScope: 'accounting' },
    { name: t("nav.accounting.otherAccounts"), href: "/accounting/other-accounts", icon: Wallet, requiresScope: 'accounting' },
    { type: 'heading', name: t("nav.accounting.headings.moneyIn") },
    { name: t("nav.accounting.invoices"), href: "/accounting/receivables", icon: FileText, requiresScope: 'accounting' },
    { name: t("nav.accounting.salesChannels"), href: "/accounting/sales-channels", icon: ShoppingCart, featureFlag: 'sales_channels', requiresScope: 'accounting' },
    { name: t("nav.accounting.funds"), href: "/accounting/funds", icon: CreditCard, requiresScope: 'accounting' },
    { type: 'heading', name: t("nav.accounting.headings.moneyOut") },
    { name: t("nav.accounting.bills"), href: "/accounting/payables", icon: Receipt, requiresScope: 'accounting' },
    { name: t("nav.accounting.expenses"), href: "/accounting/expenses", icon: BadgeDollarSign, requiresScope: 'accounting' },
    { type: 'heading', name: t("nav.accounting.headings.other") },
    { name: t("nav.accounting.transfers"), href: "/accounting/transfers", icon: ArrowRightLeft, spaceBefore: true, requiresScope: 'accounting' },
    { name: t("nav.accounting.transactions"), href: "/accounting/transactions", icon: Activity, requiresScope: 'accounting' },
    { name: t("nav.accounting.recurring"), href: "/accounting/recurring", icon: Repeat, featureFlag: 'recurring_documents', requiresScope: 'accounting' },
    { name: t("nav.accounting.budgets"), href: "/accounting/budgets", icon: Banknote, featureFlag: 'accounting_budgets', requiresScope: 'accounting' },
    { name: t("nav.accounting.tax"), href: "/accounting/tax", icon: Percent, requiresScope: 'accounting' },
  ],

  // Resources section
  "/resources": [
    { type: 'heading', name: t("nav.resources.headings.internal") },
    { name: t("nav.resources.things"), href: "/resources/assets", icon: Package },
    { name: t("nav.resources.places"), href: "/resources/places", icon: MapPin },
    { name: t("nav.resources.businesses"), href: "/resources/businesses", icon: Building2 },
    { name: t("nav.resources.people"), href: "/resources/people", icon: Users },
    { name: t("nav.resources.interactions"), href: "/resources/people/interactions", icon: MessagesSquare, featureFlag: 'interactions' },
    { name: t("nav.resources.surveys"), href: "/resources/people/surveys", icon: ClipboardList, featureFlag: 'surveys', requiresScope: 'surveys' },
    { name: t("nav.resources.vault"), href: "/resources/vault", icon: FileText },
    { type: 'heading', name: t("nav.resources.headings.external") },
    { name: t("nav.resources.fundingOpportunities"), href: "/resources/funding-opportunities", icon: Award },
  ],

  // Settings section (organization settings)
  "/settings": [
    { name: t("nav.settings.organization"), href: "/settings", icon: Building2 },
    { name: t("nav.settings.team"), href: "/settings/team", icon: Users },
    { name: t("nav.settings.aiConnectors"), href: "/settings/ai-connectors", icon: Plug, featureFlag: 'mcp_access' },
    { name: t("nav.settings.usage"), href: "/settings/usage", icon: Gauge, requiresOrgAdmin: true, featureFlag: 'usage_tracking' },
    { name: t("nav.settings.subscription"), href: "/settings/subscription", icon: BadgeCheck, requiresOrgAdmin: true, featureFlag: "subscriptions" },
    { type: 'heading', name: t("nav.settings.headings.accounting") },
    { name: t("nav.settings.configuration"), href: "/settings/accounting", icon: Settings, requiresScope: 'accounting' },
    { name: t("nav.settings.chartOfAccounts"), href: "/settings/accounts", icon: Wallet, requiresScope: 'accounting' },
    { name: t("nav.settings.tax"), href: "/settings/tax", icon: Receipt, requiresScope: 'accounting' },
    { name: t("nav.settings.emissionFactors"), href: "/settings/emission-factors", icon: Leaf },
    { name: t("nav.settings.emissionBindings"), href: "/settings/emission-bindings", icon: Leaf, featureFlag: 'places_emissions' },
    { name: t("nav.settings.carbonFormulas"), href: "/settings/carbon-formulas", icon: Leaf },
    { type: 'heading', name: t("nav.settings.headings.people") },
    { name: t("nav.settings.connectionTypes"), href: "/settings/connection-types", icon: Link2 },
    { type: 'heading', name: t("nav.settings.headings.lists") },
    { name: t("nav.settings.businessTypes"), href: "/settings/business-types", icon: Building2 },
    { name: t("nav.settings.projectTypes"), href: "/settings/project-types", icon: FolderOpen },
    { name: t("nav.settings.thingTypes"), href: "/settings/asset-types", icon: Package },
    { name: t("nav.settings.procurementTypes"), href: "/settings/procurement-types", icon: ShoppingCart },
    { name: t("nav.settings.recycledContentTypes"), href: "/settings/recycled-content-types", icon: Recycle },
    { name: t("nav.settings.endOfLifePlans"), href: "/settings/end-of-life-plans", icon: Trash2 },
    { name: t("nav.settings.wasteTreatmentMethods"), href: "/settings/waste-treatment-methods", icon: Flame },
    { name: t("nav.settings.interactionTypes"), href: "/settings/interaction-types", icon: MessagesSquare, featureFlag: 'interactions' },
    { name: t("nav.settings.interactionRoles"), href: "/settings/interaction-roles", icon: UserCog, featureFlag: 'interactions' },
    { type: 'heading', name: t("nav.settings.headings.places") },
    { name: t("nav.settings.placeFields"), href: "/settings/place-fields", icon: MapPin },
    { name: t("nav.settings.placeFieldMapping"), href: "/settings/place-field-mapping", icon: ClipboardCheck },
    { type: 'heading', name: t("nav.settings.headings.compliance") },
    { name: t("nav.settings.privacy"), href: "/settings/privacy", icon: ShieldCheck },
    { name: t("nav.settings.auditLog"), href: "/settings/audit-log", icon: History, requiresScope: 'accounting_admin', superAdminBypass: true },
  ],

  // Level Up — the school-facing dashboard, then the builder for an
  // assessment owner (docs/LEVEL_UP.md Phase 2). Inside the editor the
  // Breadcrumb is the way back, so no `end` override.
  "/level-up": [
    { name: t("nav.levelUpNav.dashboard"), href: "/level-up", icon: LayoutDashboard },
    { type: 'heading', name: t("nav.levelUpNav.manage") },
    { name: t("nav.levelUpNav.assessments"), href: "/level-up/manage", icon: ClipboardCheck, featureFlag: 'level_up_authoring', requiresScope: 'level_up' },
  ],

  // Reports section
  "/reports": [
    { name: t("nav.reports.all"), href: "/reports/all", icon: FileBarChart },
    { name: t("nav.reports.recent"), href: "/reports/recent", icon: Activity },
  ],
});

// Factory function for detail page secondary nav with i18n support
export const getDetailPageSecondaryNav = (t: TFunction) => ({
  project: (code: string): NavigationItem[] => [
    { name: t("nav.projectDetail.overview"), href: `/projects/${code}/overview`, icon: LayoutDashboard },
    { name: t("nav.projectDetail.accounting"), href: `/projects/${code}/accounting`, icon: PieChart, requiresScope: 'accounting' },
    { name: t("nav.projectDetail.events"), href: `/projects/${code}/events`, icon: CalendarRange, featureFlag: 'project_events' },
    { name: t("nav.projectDetail.sustainability"), href: `/projects/${code}/sustainability`, icon: Leaf },
    // A break, then the project's people, things, places and discussion.
    { name: t("nav.projectDetail.team"), href: `/projects/${code}/team`, icon: Users, spaceBefore: true },
    { name: t("nav.projectDetail.volunteers"), href: `/projects/${code}/volunteers`, icon: HandHeart, featureFlag: 'volunteer_self_signup' },
    { name: t("nav.projectDetail.interactions"), href: `/projects/${code}/interactions`, icon: MessagesSquare, featureFlag: 'interactions' },
    { name: t("nav.projectDetail.surveys"), href: `/projects/${code}/surveys`, icon: ClipboardList, featureFlag: 'surveys', requiresScope: 'surveys' },
    { name: t("nav.projectDetail.things"), href: `/projects/${code}/assets`, icon: Package },
    { name: t("nav.projectDetail.places"), href: `/projects/${code}/places`, icon: MapPin },
    { name: t("nav.projectDetail.channels"), href: `/projects/${code}/channels`, icon: MessageSquare, badgeKey: 'channel_unread' },
  ],
});

// Helper function to strip the /org/:orgId prefix from a pathname
function stripOrgPrefix(pathname: string): string {
  const match = pathname.match(/^\/org\/[^/]+(\/.*)?$/);
  return match ? (match[1] || '/') : pathname;
}

/**
 * The `:code` of a `/projects/:code/…` path (org prefix or not), else null.
 * The same shape `getSecondaryNavigation` matches for the project detail nav;
 * `MainNavigation` uses it to key the project channels badge.
 */
export function getProjectCodeFromPath(pathname: string): string | null {
  const match = /^\/projects\/([^/]+)/.exec(stripOrgPrefix(pathname));
  return match ? match[1] : null;
}

// Helper function to get secondary navigation based on current path with i18n support
export function getSecondaryNavigation(pathname: string, t: TFunction): SecondaryNavEntry[] | null {
  // Strip the /org/:orgId prefix if present
  const normalizedPath = stripOrgPrefix(pathname);

  const config = getSecondaryNavigationConfig(t);
  const detailNav = getDetailPageSecondaryNav(t);

  // Check for direct matches first (accounting, settings)
  for (const key in config) {
    if (normalizedPath.startsWith(key)) {
      return config[key];
    }
  }

  // Check for detail pages
  const projectCode = getProjectCodeFromPath(pathname);
  if (projectCode) {
    return detailNav.project(projectCode);
  }

  return null;
}

/**
 * The secondary nav the caller may see: every item — and every item inside a
 * group — through the one visibility rule (`isNavItemVisible`), groups left
 * empty dropped, then headings left with nothing under them.
 */
export function visibleSecondaryNav(
  entries: SecondaryNavEntry[],
  ctx: NavVisibilityContext,
): SecondaryNavEntry[] {
  const filtered = entries
    .map((entry) =>
      entry.type === 'group'
        ? { ...entry, items: entry.items.filter((item) => isNavItemVisible(item, ctx)) }
        : entry,
    )
    .filter((entry) => {
      if (entry.type === 'heading') return true;
      if (entry.type === 'group') return entry.items.length > 0;
      return isNavItemVisible(entry, ctx);
    });
  return dropEmptyHeadings(filtered);
}
